import { useState, useEffect, createContext, useContext } from 'react'
import { IonLoading } from '@ionic/react'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'

import { usePushToken } from '../utils/use-push-token'
import { updateInstallation } from './api'

import type { Children } from '../types'

const pushNotificationContext = createContext<{ token: string | null } | null>(
  null
)

function PushNotificationContextProvider({ children }: Children) {
  const [permission, setPermission] = useState<'granted' | 'denied' | null>(
    null
  )

  const tokenQuery = usePushToken({ enabled: permission === 'granted' })

  useEffect(() => {
    FirebaseMessaging.isSupported().then((supported) => {
      console.log('isSupported: ', supported.isSupported)
      if (supported.isSupported) {
        FirebaseMessaging.requestPermissions().then((status) => {
          console.log('requested permission: ', status.receive)
          if (status.receive === 'granted') {
            setPermission('granted')
          }
          if (status.receive === 'denied') setPermission('denied')
        })
      } else {
        setPermission('denied')
      }
    })
  }, [])

  useEffect(() => {
    if (!tokenQuery.data) return

    updateInstallation(tokenQuery.data)
  }, [tokenQuery.data])

  if (permission === 'denied') {
    return (
      <pushNotificationContext.Provider value={{ token: null }}>
        {children}
      </pushNotificationContext.Provider>
    )
  }

  if (permission === 'granted') {
    if (tokenQuery.status === 'success') {
      return (
        <pushNotificationContext.Provider value={{ token: tokenQuery.data }}>
          {children}
        </pushNotificationContext.Provider>
      )
    }
    if (tokenQuery.status === 'error') {
      return <p>Error:</p>
    }

    return <IonLoading isOpen message="Loading..." />
  }
}

function usePushNotificationToken() {
  const pushToken = useContext(pushNotificationContext)

  if (!pushToken) {
    throw new Error('PushNotificationContext: No value provided')
  }

  return { pushToken: pushToken.token }
}

export { PushNotificationContextProvider, usePushNotificationToken }
